import React from 'react';
import { useLocations } from '@/domain/events/useEvents';
import {
  saveTransferStop,
  type TransferRecord,
  type TransferRecordWaypoint,
  type TransferStopInput,
  type TransferStopKind,
} from '@/data/transferRecord';
import TransferRecordDialog from './TransferRecordDialog';
import { STOP_KINDS, fromSiteInputValue, siteZoneAbbreviation, toSiteInputValue } from './transferRecordView';
import type { TransferActionRunner } from './useTransferRecord';

type PlaceKind = 'site' | 'address';

type TransferStopDialogProps = {
  record: TransferRecord;
  /** The stop to edit; null adds one (it goes last on the route). */
  stop: TransferRecordWaypoint | null;
  runner: TransferActionRunner;
  onClose: () => void;
  onDone: (message: string) => void;
};

type StopFields = {
  place: PlaceKind;
  locationId: string;
  address: string;
  kind: string;
  arrive: string;
  depart: string;
  notes: string;
};

/** Add or edit a stop between the two sites: a Portal site or a street address, with its times. */
export default function TransferStopDialog({ record, stop, runner, onClose, onDone }: TransferStopDialogProps) {
  const { transfer } = record;
  const locationsQuery = useLocations({ allowedLocationIds: null });
  const locations = React.useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const zoneFor = React.useCallback((locationId: string, place: PlaceKind) => {
    if (place === 'address') return transfer.toSite?.timezone ?? null;
    return locations.find((location) => location.location_id === locationId)?.timezone ?? transfer.toSite?.timezone ?? null;
  }, [locations, transfer.toSite?.timezone]);

  const initial = React.useMemo<StopFields>(() => {
    // An address stop still names a site in the table (the receiving one), so the address decides.
    const place: PlaceKind = stop?.address ? 'address' : 'site';
    const locationId = stop?.location?.id ?? '';
    return {
      place,
      locationId,
      address: stop?.address ?? '',
      kind: stop?.kind ?? 'stop',
      arrive: '',
      depart: '',
      notes: stop?.notes ?? '',
    };
  }, [stop]);
  const [fields, setFields] = React.useState<StopFields>(initial);
  // Times are filled once the site's zone is known, and only sent when they change.
  const [timesTouched, setTimesTouched] = React.useState({ arrive: false, depart: false });
  const zone = zoneFor(fields.locationId, fields.place);
  React.useEffect(() => {
    setFields((current) => ({
      ...current,
      arrive: timesTouched.arrive ? current.arrive : toSiteInputValue(stop?.arriveAt, zone),
      depart: timesTouched.depart ? current.depart : toSiteInputValue(stop?.departAt, zone),
    }));
  }, [stop?.arriveAt, stop?.departAt, timesTouched.arrive, timesTouched.depart, zone]);

  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const ids = {
    place: React.useId(),
    site: React.useId(),
    address: React.useId(),
    kind: React.useId(),
    arrive: React.useId(),
    depart: React.useId(),
    notes: React.useId(),
  };
  const abbreviation = siteZoneAbbreviation(zone);

  function patch(next: Partial<StopFields>) {
    setFields((current) => ({ ...current, ...next }));
    setError(null);
  }

  function handlePlace(event: React.ChangeEvent<HTMLInputElement>) {
    patch({ place: event.target.value === 'address' ? 'address' : 'site' });
  }

  function handleSite(event: React.ChangeEvent<HTMLSelectElement>) {
    patch({ locationId: event.target.value });
  }

  function handleAddress(event: React.ChangeEvent<HTMLInputElement>) {
    patch({ address: event.target.value });
  }

  function handleKind(event: React.ChangeEvent<HTMLSelectElement>) {
    patch({ kind: event.target.value });
  }

  function handleArrive(event: React.ChangeEvent<HTMLInputElement>) {
    setTimesTouched((current) => ({ ...current, arrive: true }));
    patch({ arrive: event.target.value });
  }

  function handleDepart(event: React.ChangeEvent<HTMLInputElement>) {
    setTimesTouched((current) => ({ ...current, depart: true }));
    patch({ depart: event.target.value });
  }

  function handleNotes(event: React.ChangeEvent<HTMLTextAreaElement>) {
    patch({ notes: event.target.value });
  }

  function buildInput(): TransferStopInput | string {
    const input: TransferStopInput = { id: stop?.id ?? null };
    if (fields.place === 'site') {
      if (!fields.locationId) return 'Choose the site, or switch to an address.';
      if (!stop || fields.locationId !== (stop.location?.id ?? '')) input.location_id = fields.locationId;
      if (stop?.address) input.address = null;
    } else {
      const address = fields.address.trim();
      if (!address) return 'Enter the street address, or choose a site.';
      if (!stop || address !== (stop.address ?? '')) input.address = address;
    }
    if (!stop || fields.kind !== (stop.kind ?? 'stop')) input.kind = fields.kind as TransferStopKind;
    const arrive = fields.arrive ? fromSiteInputValue(fields.arrive, zone) : null;
    const depart = fields.depart ? fromSiteInputValue(fields.depart, zone) : null;
    if (arrive && depart && Date.parse(depart) < Date.parse(arrive)) return 'Leaving must be after arriving.';
    if (!stop || timesTouched.arrive) input.arrive_at = arrive;
    if (!stop || timesTouched.depart) input.depart_at = depart;
    const notes = fields.notes.trim() || null;
    if (!stop || notes !== (stop.notes ?? null)) input.notes = notes;
    return input;
  }

  async function save() {
    const built = buildInput();
    if (typeof built === 'string') {
      setError(built);
      return;
    }
    if (stop && Object.keys(built).length === 1) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        stop ? `stop:${stop.id}` : 'stop-add',
        built,
        (requestKey) => saveTransferStop(transfer.id, requestKey, built),
        { fallback: 'The stop was not saved.' },
      );
      onDone(stop ? 'Stop saved' : 'Stop added');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The stop was not saved.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!stop) return;
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    const input: TransferStopInput = { id: stop.id, delete: true };
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        `stop-remove:${stop.id}`,
        input,
        (requestKey) => saveTransferStop(transfer.id, requestKey, input),
        { fallback: 'The stop was not removed.' },
      );
      onDone('Stop removed');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The stop was not removed.');
      setConfirmRemove(false);
    } finally {
      setSaving(false);
    }
  }

  function handleRemoveClick() {
    void remove();
  }

  return (
    <TransferRecordDialog
      title={stop ? 'Edit stop' : 'Add a stop'}
      eyebrow={transfer.docNo ?? 'Transfer'}
      subtitle="Stops sit between the two sites, in the order they are added."
      onClose={onClose}
      busy={saving}
      error={error}
      onSubmit={() => { void save(); }}
      footer={(
        <>
          {stop ? (
            <button type="button" className="btn danger transfer-record-dialog__remove" onClick={handleRemoveClick} disabled={saving}>
              {confirmRemove ? 'Remove this stop?' : 'Remove stop'}
            </button>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : stop ? 'Save stop' : 'Add stop'}
          </button>
        </>
      )}
    >
      <fieldset className="transfer-record-fieldset">
        <legend className="transfer-record-fieldset__legend">Where</legend>
        <div className="transfer-record-radios" role="radiogroup" aria-labelledby={ids.place}>
          <span id={ids.place} className="sr-only">Stop place</span>
          <label className="transfer-record-radio">
            <input type="radio" name={ids.place} value="site" checked={fields.place === 'site'} onChange={handlePlace} />
            <span>A Portal site</span>
          </label>
          <label className="transfer-record-radio">
            <input type="radio" name={ids.place} value="address" checked={fields.place === 'address'} onChange={handlePlace} />
            <span>A street address</span>
          </label>
        </div>
        <div className="transfer-record-form-grid">
          {fields.place === 'site' ? (
            <div className="transfer-record-field">
              <label className="transfer-record-field__label" htmlFor={ids.site}>Site</label>
              <select
                id={ids.site}
                className="transfer-record-field__control"
                value={fields.locationId}
                onChange={handleSite}
                disabled={locationsQuery.isLoading}
                data-autofocus="true"
              >
                <option value="">{locationsQuery.isLoading ? 'Loading sites…' : 'Choose a site'}</option>
                {locations.map((location) => (
                  <option key={location.location_id} value={location.location_id}>{location.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="transfer-record-field">
              <label className="transfer-record-field__label" htmlFor={ids.address}>Street address</label>
              <input
                id={ids.address}
                className="transfer-record-field__control"
                value={fields.address}
                onChange={handleAddress}
                autoComplete="street-address"
                placeholder="Street, city, state"
                maxLength={300}
                data-autofocus="true"
              />
            </div>
          )}
          <div className="transfer-record-field">
            <label className="transfer-record-field__label" htmlFor={ids.kind}>Kind of stop</label>
            <select id={ids.kind} className="transfer-record-field__control" value={fields.kind} onChange={handleKind}>
              {STOP_KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
            </select>
          </div>
        </div>
        {locationsQuery.isError && fields.place === 'site' ? (
          <p className="transfer-record-field__hint" role="status">Sites could not be loaded. Use a street address, or try again later.</p>
        ) : null}
      </fieldset>
      <fieldset className="transfer-record-fieldset">
        <legend className="transfer-record-fieldset__legend">When{abbreviation ? ` (${abbreviation})` : ''}</legend>
        <div className="transfer-record-form-grid">
          <div className="transfer-record-field">
            <label className="transfer-record-field__label" htmlFor={ids.arrive}>Arrives</label>
            <input id={ids.arrive} className="transfer-record-field__control" type="datetime-local" value={fields.arrive} onChange={handleArrive} />
          </div>
          <div className="transfer-record-field">
            <label className="transfer-record-field__label" htmlFor={ids.depart}>Leaves</label>
            <input id={ids.depart} className="transfer-record-field__control" type="datetime-local" value={fields.depart} onChange={handleDepart} />
          </div>
          <div className="transfer-record-field transfer-record-field--full">
            <label className="transfer-record-field__label" htmlFor={ids.notes}>Notes</label>
            <textarea
              id={ids.notes}
              className="transfer-record-field__control transfer-record-field__control--text"
              value={fields.notes}
              onChange={handleNotes}
              rows={2}
              placeholder="Optional, e.g. dock door or contact"
              maxLength={1000}
            />
          </div>
        </div>
      </fieldset>
    </TransferRecordDialog>
  );
}
